import {
  cancelForAnyReason,
  checkInBaggage,
  climateCompensation,
  FAST_TRACK,
  flexibleTicket,
  getAvailabilityList,
  getCartAvailability,
  getTranslatedTexts,
  LOUNGE,
  validateFastTrackAvailabilityTranslation,
  validateLoungeAvailabilityTranslation,
} from '@eti/products';
import sumBy from 'lodash/sumBy';
import type {
  BoundNew,
  Maybe,
  ProductSegmentAvailabilityText,
  SellSpecificationBaggageNew,
  SellSpecificationNoneNew,
  SellSpecificationTravelerNew,
} from '@eti/schema-types';
import { price as priceUtils } from '@eti/utils';
import type { AvailableExtraProductsType } from '../v3/branded-fares/model';

const { id: FAST_TRACK_ID, name: FAST_TRACK_NAME } = FAST_TRACK;
const { id: LOUNGE_ID, name: LOUNGE_NAME } = LOUNGE;

const AVAILABILITY_PRODUCT_NAME = {
  [FAST_TRACK_ID]: FAST_TRACK_NAME,
  [LOUNGE_ID]: LOUNGE_NAME,
};

export type textTypes = {
  additionalInformation?: string;
  airportAvailability: string;
  description: string;
  readMoreContent: string;
  title: string;
  upSell: string;
};

type supportedAvailabilityProductNameTypes = 'fastTrack' | 'lounge';
export const availabilityTexts: Record<supportedAvailabilityProductNameTypes, textTypes> = {
  [FAST_TRACK_NAME]: {
    readMoreContent: 'fastTrackReadMoreModalContent',
    description: 'fastTrackAbstract',
    title: 'fastTrackTitle',
    upSell: 'fastTrackUpSellText',
    airportAvailability: 'fasttrackGeneralPurpose',
  },
  [LOUNGE_NAME]: {
    additionalInformation: 'loungeAdditionalInformation',
    description: 'loungeAbstract',
    readMoreContent: 'loungeReadMoreModalContent',
    title: 'loungeTitle',
    upSell: 'loungeUpSellText',
    airportAvailability: 'loungeGeneralPurpose',
  },
};

const parseJsonProp = (prop: string) => {
  try {
    return JSON.parse(prop);
  } catch (error) {
    return null;
  }
};

export const getAvailabilityProductName = (productTypeId: string) => {
  const availabilityProductName = AVAILABILITY_PRODUCT_NAME[
    Number(productTypeId) as keyof typeof AVAILABILITY_PRODUCT_NAME
  ] as supportedAvailabilityProductNameTypes;

  return availabilityProductName;
};

const UPSELL_TYPES = {
  PROMOTIONAL: 'promotional',
  INFOMATION: 'information',
  INCLUDED: 'included',
} as const;
type UpSellKeys = keyof typeof UPSELL_TYPES;
type UpSellTypes = (typeof UPSELL_TYPES)[UpSellKeys];

type UpSellType = {
  content: string;
  type?: UpSellTypes;
};

export const AvailabilityType = {
  PerBound: 0,
  PerSegment: 1,
} as const;

export const getIncludedProductPrice = (
  availableExtraProducts: AvailableExtraProductsType[],
  selectedExtraProducts: number[],
) => {
  return selectedExtraProducts.reduce((totalPrice: number, id: number) => {
    const selectedProduct = availableExtraProducts.find(
      (product) => product.productId === String(id),
    );
    const sellSpecification = selectedProduct?.sellSpecification;
    let productTotalprice = 0;

    if (sellSpecification) {
      if (id === flexibleTicket.id) {
        const sellSpecificationTravelers = sellSpecification as SellSpecificationTravelerNew;
        const flexibleTicketPrices = sellSpecificationTravelers?.sellPriceTravelers;
        productTotalprice = sumBy(flexibleTicketPrices, 'price.price.value');
      } else if (id === checkInBaggage.id) {
        const sellSpecificationBaggage = sellSpecification as SellSpecificationBaggageNew;
        const checkInBaggageSellPriceBaggage = sellSpecificationBaggage?.sellPriceBaggage?.flatMap(
          (baggage) => baggage?.options,
        );
        productTotalprice = sumBy(checkInBaggageSellPriceBaggage, 'price.price.value');
      } else if (id === cancelForAnyReason.id) {
        const cfarTicketSellSpecification = sellSpecification;
        productTotalprice = sumBy([cfarTicketSellSpecification], 'price.price.value');
      } else if (id === climateCompensation.id) {
        const climateCompensationSellSpecification = sellSpecification as SellSpecificationNoneNew;
        productTotalprice = sumBy([climateCompensationSellSpecification], 'price.price.value');
      }
    }

    return totalPrice + productTotalprice;
  }, 0);
};

export const getAvailabilityProduct = (
  availableExtraProducts: AvailableExtraProductsType[],
  productTypeId: string,
) =>
  availableExtraProducts.find((product) => {
    return product.productId === productTypeId;
  });

const getAvailabilityPerType = (productTypeId: string) => {
  return productTypeId === LOUNGE_ID.toString()
    ? AvailabilityType.PerSegment
    : AvailabilityType.PerBound;
};

export const getAvailability = (
  availableExtraProducts: AvailableExtraProductsType[],
  bounds: BoundNew[],
  productTypeId: string,
) => {
  const availabilityProduct = getAvailabilityProduct(availableExtraProducts, productTypeId);
  const productSpecificTexts = availabilityProduct?.productSpecificTexts;
  const availabilityPerType = getAvailabilityPerType(productTypeId);

  const availabilityList = getAvailabilityList(
    bounds,
    (productSpecificTexts as any)?.productSegmentAvailabilityTexts ?? [],
    availabilityPerType,
  );

  return availabilityList;
};

export const getAvailabilityCart = (
  bounds: BoundNew[],
  numberOfTraveleras: number,
  productTypeId: string,
  t: () => string,
  productSegmentAvailabilityTexts: Maybe<Maybe<ProductSegmentAvailabilityText>[]> | undefined,
) => {
  const availabilityPerType = getAvailabilityPerType(productTypeId);
  const availabilityList = getAvailabilityList(
    bounds,
    productSegmentAvailabilityTexts ?? [],
    availabilityPerType,
  );

  if (availabilityList.length === 0) {
    return null;
  }

  const availabilityProductName = getAvailabilityProductName(productTypeId);
  const airportAvailabilityTexts = getTranslatedTexts(
    availabilityTexts[availabilityProductName],
    t,
  );

  const availabilityProps = parseJsonProp(airportAvailabilityTexts?.airportAvailability);
  const cartTranslation = availabilityProps?.cart?.purchasedMessage;
  const availability = getCartAvailability(
    numberOfTraveleras,
    cartTranslation,
    availabilityList as ProductSegmentAvailabilityText[],
  );

  return availability;
};

export const getPrice = (availabilityProduct?: AvailableExtraProductsType) => {
  const priceRaw = availabilityProduct?.sellSpecification?.price?.price?.value ?? 0;
  const price = priceUtils.formatPrice(priceRaw);
  return { price, priceRaw };
};

export const getProductdata = (
  availabilityList: ProductSegmentAvailabilityText[],
  availableExtraProducts: AvailableExtraProductsType[],
  name: string,
  priceLabel: string,
  productTypeId: string,
  translation: textTypes,
  shouldDisplayUpsell: boolean,
  upSellObject?: UpSellType,
) => {
  const { airportAvailability, description, readMoreContent, title } = translation;
  const { additionalInformation } = translation ?? { additionalInformation: '' };
  const availabilityProps = parseJsonProp(airportAvailability);
  const availabilityProduct = getAvailabilityProduct(availableExtraProducts, productTypeId);
  const { price, priceRaw } = getPrice(availabilityProduct);

  const productId = Number(productTypeId);
  const productName = name;
  const illustrationUrl = '';

  const extraProps = {
    availabilityList,
    availabilityTranslation: availabilityProps,
  };

  return {
    additionalInformation,
    airportAvailability,
    description,
    extraProps,
    illustrationUrl,
    price,
    priceLabel,
    priceRaw,
    productId,
    productName,
    readMoreContent,
    title,
    shouldDisplayUpsell,
    upSell: upSellObject,
  };
};

export const validateAvailabilityTranslation = (
  productTypeId: string,
  airportAvailability?: string,
) => {
  if (!airportAvailability) {
    return false;
  }

  const parsedAirportAvailability = parseJsonProp(airportAvailability);
  if (!parsedAirportAvailability) {
    return false;
  }

  switch (productTypeId) {
    case FAST_TRACK_ID.toString():
      return validateFastTrackAvailabilityTranslation(parsedAirportAvailability);
    case LOUNGE_ID.toString():
      return validateLoungeAvailabilityTranslation(parsedAirportAvailability);
    default:
      return false;
  }
};
